import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

const SYNC_INTERVAL_MS = 60 * 1000; // 1 minute

/**
 * Global email sync hook — polls /api/emails/sync every 60 seconds
 * while the app is open and the tab is visible. Invalidates email
 * queries when new emails are synced so UI updates automatically.
 */
export function useEmailSync() {
  const queryClient = useQueryClient();
  const syncInProgress = useRef(false);

  useEffect(() => {
    async function syncEmails() {
      if (syncInProgress.current) return;
      if (document.hidden) return; // Skip if tab is backgrounded

      syncInProgress.current = true;
      try {
        const res = await fetch("/api/emails/sync?days=1&limit=50", {
          credentials: "include",
        });
        if (!res.ok) return;

        const data = await res.json();
        if (data.synced > 0) {
          // Invalidate all email-related queries so the UI refreshes
          queryClient.invalidateQueries({ queryKey: ["company-emails"] });
          queryClient.invalidateQueries({ queryKey: ["synced-emails"] });
          queryClient.invalidateQueries({ queryKey: ["activities"] });
          queryClient.invalidateQueries({ queryKey: ["company-activities"] });
          queryClient.invalidateQueries({ queryKey: ["contact-activities"] });
          queryClient.invalidateQueries({ queryKey: ["timeline"] });
        }
      } catch {
        // Silent fail — user may not have Graph token (localhost, no SSO)
      } finally {
        syncInProgress.current = false;
      }
    }

    // Initial sync on mount
    syncEmails();

    // Poll every 60 seconds
    const interval = setInterval(syncEmails, SYNC_INTERVAL_MS);

    // Also sync when tab becomes visible again
    function handleVisibilityChange() {
      if (!document.hidden) syncEmails();
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [queryClient]);
}
